import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, ChevronDownIcon, CloseIcon, SearchIcon } from '../icons';

export interface FilterOption {
  id: string;
  label: string;
}

export interface FilterAccordionProps {
  title: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (id: string) => void;
}

/** One collapsible filter group with a searchable, multi-select option list. */
export function FilterAccordion({
  title,
  options,
  selected,
  onToggle,
}: FilterAccordionProps): ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();

    return needle === ''
      ? options
      : options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, query]);

  return (
    <div className="overflow-hidden rounded-[var(--radius-card)] bg-ink-850">
      <button
        type="button"
        onClick={() => {
          setOpen((current) => !current);
        }}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <span className={open ? 'font-semibold text-fog-100' : 'text-fog-500'}>
          {title}
          {selected.length > 0 && (
            <span className="ml-2 text-sm text-primary">({selected.length})</span>
          )}
        </span>
        <ChevronDownIcon
          className={`size-5 shrink-0 text-fog-100 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-4">
          <div className="relative mb-3">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-fog-500" />
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
              }}
              placeholder={t('filters.searchPlaceholder')}
              aria-label={`${title} — ${t('filters.searchPlaceholder')}`}
              className="w-full rounded-full border border-ink-600 bg-transparent py-3 pr-11 pl-12 font-semibold text-fog-100 placeholder:font-normal placeholder:text-fog-600 focus:ring-2 focus:ring-primary focus:outline-none"
            />
            {query !== '' && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                }}
                aria-label={t('common.close')}
                className="absolute top-1/2 right-3 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-ink-600 text-fog-300"
              >
                <CloseIcon className="size-4" />
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            <p className="px-1 py-2 text-sm text-fog-500">{t('filters.noOptions')}</p>
          ) : (
            <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
              {visible.map((option) => {
                const checked = selected.includes(option.id);

                return (
                  <li key={option.id}>
                    <button
                      type="button"
                      onClick={() => {
                        onToggle(option.id);
                      }}
                      aria-pressed={checked}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                        checked ? 'bg-ink-700 text-fog-100' : 'bg-ink-900 text-fog-500'
                      }`}
                    >
                      <span
                        className={`flex size-6 shrink-0 items-center justify-center rounded-md border ${
                          checked ? 'border-fog-100 text-fog-100' : 'border-ink-600'
                        }`}
                      >
                        {checked && <CheckIcon className="size-4" />}
                      </span>
                      <span className="text-sm">{option.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
